import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Skeleton, Text, UnstyledButton } from '@mantine/core';
import { useSearchParams, useNavigate } from 'react-router-dom';
import type { Exercise, ExerciseKind, LineRef } from '@nelnela/contracts';
import { useStudy } from '../app/study';
import { ERROR_INK, ERROR_RULE } from '../lib/error-tint';
import { api, ApiError } from '../lib/api';
import { startActivity, type ActivityHandle } from '../lib/session';
import { ulid } from '../lib/ulid';
import { ttsPlayer, ttsCalmLine } from '../lib/tts-player';
import {
  acceptOptions,
  answered,
  answerLatencyMs,
  expectedText,
  currentExercise,
  dismissOptions,
  initRun,
  isDone,
  keystroke,
  next as advance,
  skip as skipPrompt,
  replaceTail,
  scopedItemIds,
  summarise,
  summaryLine,
  type RunState,
} from '../lib/review-run';
import { IconBack, IconPlay } from '../components/watch/icons';

/**
 * F11 Review — one prompt per screen (design `5d`, `5e`, `12c`).
 *
 * The screen is deliberately thin: `lib/review-run.ts` holds the run's state and the server
 * holds every judgement. What lives here is the shape of one prompt and the timing of one
 * answer — including the two things only a screen can know, which are when the prompt appeared
 * (latency is measured from the paint, not from the fetch) and what the learner's first
 * keystroke was.
 *
 * The prompt hugs the bottom of the screen (`5d`): a spacer above it pushes it down to where a
 * thumb is, and where the on-screen keyboard will not cover it.
 */

const BLANK = '___';

/** A span bound from the query string: finite or absent, never NaN. */
function spanParam(raw: string | null): number | undefined {
  if (raw === null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/**
 * The due items inside the video span the learner chose, or null when the run is not scoped.
 *
 * `reviewHref` puts the span in the link and `VocabSheet` claims "the server re-reads what is due
 * inside it at the moment the run is built" — which nothing implemented, on either side, so
 * "Review these (6)" ran the learner's whole due list instead of the six words on screen.
 *
 * A failure here returns null rather than throwing: an unscoped review is a worse run than a
 * scoped one, and a great deal better than an error where a review should be.
 */
async function dueInSpan(scope: {
  videoId?: string | undefined;
  itemIds?: string[] | undefined;
  startMs?: number | undefined;
  endMs?: number | undefined;
}): Promise<{ itemIds: string[]; count: number } | null> {
  if (!scope.videoId || scope.itemIds) return null;
  try {
    const res = await api.videoItems(scope.videoId, {
      ...(scope.startMs !== undefined ? { startMs: scope.startMs } : {}),
      ...(scope.endMs !== undefined ? { endMs: scope.endMs } : {}),
    });
    return scopedItemIds(res.items);
  } catch {
    return null;
  }
}

export function Review() {
  const [params] = useSearchParams();
  const study = useStudy();
  const navigate = useNavigate();

  // Read once, at mount: a run is a run, and re-reading the query string mid-run would restart it.
  const [scope] = useState(() => ({
    kinds: params.get('kinds')?.split(',').filter(Boolean) as ExerciseKind[] | undefined,
    itemIds: params.get('itemIds')?.split(',').filter(Boolean),
    exerciseIds: params.get('exerciseIds')?.split(',').filter(Boolean),
    storyId: params.get('storyId') ?? undefined,
    dailyBiteId: params.get('dailyBiteId') ?? undefined,
    planIdx: params.get('planIdx') ? Number(params.get('planIdx')) : undefined,
    videoId: params.get('video') ?? undefined,
    // The vocab sheet links with the SPAN, not twelve uuids — a URL of ids is enormous and stale
    // the moment the fold runs. The due items inside it are resolved here, at mount, which is the
    // moment the run is built.
    startMs: spanParam(params.get('startMs')),
    endMs: spanParam(params.get('endMs')),
    count: params.get('count') ? Number(params.get('count')) : undefined,
  }));

  const [run, setRun] = useState<RunState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activityRef = useRef<ActivityHandle | null>(null);

  useEffect(() => {
    let cancelled = false;
    let handle: ActivityHandle | null = null;
    void (async () => {
      try {
        const target = scope.videoId ? { videoId: scope.videoId } : undefined;
        const a = await startActivity(study.session, 'review', target, {
          ...(scope.dailyBiteId !== undefined && scope.planIdx !== undefined
            ? { dailyBiteId: scope.dailyBiteId, planIdx: scope.planIdx }
            : {}),
        });
        if (cancelled) {
          void a.end();
          return;
        }
        handle = a;
        activityRef.current = a;
        // "Review these (N)" on the vocabulary sheet: the run is the due words IN THIS SPAN, and
        // without this it was the learner's whole due list — six words they were just looking at
        // replaced by six they were not, with nothing on screen to say so.
        const scoped = await dueInSpan(scope);
        if (cancelled) return;
        const list = await api.exerciseNext({
          activityId: a.id,
          ...(scope.kinds ? { kinds: scope.kinds } : {}),
          ...(scoped ? { itemIds: scoped.itemIds, count: scoped.count } : {}),
          ...(scope.itemIds ? { itemIds: scope.itemIds } : {}),
          ...(scope.exerciseIds ? { exerciseIds: scope.exerciseIds } : {}),
          ...(scope.storyId ? { storyId: scope.storyId } : {}),
          ...(scope.count ? { count: scope.count } : {}),
        });
        if (!cancelled) setRun(initRun(list));
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.problem.status === 403
            ? 'Practice is part of the paid layer.'
            : 'Couldn’t start the review. It’ll be here when the connection is.',
        );
      }
    })();
    return () => {
      cancelled = true;
      activityRef.current = null;
      if (handle) void handle.end({ touchedItems: touchedRef.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study.session, scope]);

  const touchedRef = useRef<string[]>([]);

  // The tab closing mid-run still ends the activity, so the minute counts (M5's "it all counted").
  useEffect(() => {
    const onHide = () => {
      void activityRef.current?.end({ touchedItems: touchedRef.current, keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  if (error) {
    return (
      <Shell onBack={() => navigate(-1)} title="Review">
        <Text fz={13} c="var(--nn-muted)" py="lg">
          {error}
        </Text>
      </Shell>
    );
  }
  if (!run) return <RunSkeleton onBack={() => navigate(-1)} />;
  if (run.exercises.length === 0) {
    return (
      <Shell onBack={() => navigate(-1)} title="Review">
        <Text fz={13} c="var(--nn-muted)" py="lg">
          Nothing to review yet — save a few lines or watch something.
        </Text>
      </Shell>
    );
  }
  if (isDone(run)) {
    return <RunSummary run={run} onDone={() => navigate('/')} />;
  }

  return (
    <Prompt
      run={run}
      setRun={setRun}
      activityId={activityRef.current?.id ?? null}
      onTouched={(id) => {
        if (!touchedRef.current.includes(id)) touchedRef.current.push(id);
      }}
    />
  );
}

// ---------------------------------------------------------------------------------------------
// one prompt
// ---------------------------------------------------------------------------------------------

function Prompt({
  run,
  setRun,
  activityId,
  onTouched,
}: {
  run: RunState;
  setRun: Dispatch<SetStateAction<RunState | null>>;
  activityId: string | null;
  onTouched: (itemId: string) => void;
}) {
  const navigate = useNavigate();
  const ex = currentExercise(run)!;
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [calm, setCalm] = useState<string | null>(null);
  // Latency is measured from when the prompt was PAINTED, not from when it was fetched: the
  // grader's z-score is about how long the learner took to remember, and a slow network is not
  // the learner being slow.
  const shownAt = useRef(Date.now());

  useEffect(() => {
    shownAt.current = Date.now();
    setTyped('');
    setCalm(null);
  }, [ex.id]);

  const optionsMode =
    run.answerMode === 'options' || ex.kind === 'choice' || ex.kind === 'comprehension';
  // Wanting options and HAVING them are different states, and only the second one should change
  // the controls. Conflating them is what removed the Check button with no chips to replace it.
  const showingOptions = optionsMode && (ex.prompt.options ?? []).length > 0;

  const submit = useCallback(
    async (answer: unknown, mode: 'typed' | 'options' | 'shown', opts?: { advance?: boolean }) => {
      if (!activityId || busy) return;
      setBusy(true);
      try {
        const res = await api.exerciseAnswer({
          exerciseId: ex.id,
          activityId,
          answer,
          answerMode: mode,
          latencyMs: answerLatencyMs(shownAt.current, Date.now()),
          hintsUsed: 0,
          eventId: ulid(),
        });
        for (const id of ex.itemIds) onTouched(id);
        const withVerdict = answered(
          run,
          {
            correct: res.correct,
            graded: res.graded,
            expected: res.expected,
            ...(res.explanation ? { explanation: res.explanation } : {}),
          },
          mode,
        );
        // Composed, not two setRun calls: the second would be computed from a stale `run`.
        setRun(opts?.advance ? advance(withVerdict) : withVerdict);
      } catch {
        setCalm('That answer didn’t reach us. Try again in a moment.');
      } finally {
        setBusy(false);
      }
    },
    [activityId, busy, ex, onTouched, run, setRun],
  );

  const onSkip = () => setRun(skipPrompt(run));
  const onNext = () => setRun(advance(run));

  /**
   * "Answer with options — yes".
   *
   * Accepting only set a flag, and the flag made the screen render chips from an options array
   * that was empty: no chips, no caret, and the Check button gone for the rest of the run. The
   * server builds a `choice` only when `exerciseNext` is asked for one, so accepting has to ask.
   */
  const onAcceptOptions = async () => {
    const accepted = acceptOptions(run);
    setRun(accepted);
    const remaining = run.exercises.slice(run.idx);
    const itemIds = [...new Set(remaining.flatMap((e) => e.itemIds))];
    if (!activityId || itemIds.length === 0) return;
    const idxAtRequest = run.idx;
    try {
      const list = await api.exerciseNext({
        activityId,
        itemIds,
        answerMode: 'options',
        count: Math.min(12, remaining.length),
      });
      // Only if they are still on the prompt they accepted from. Answering during the fetch means
      // the tail we asked for is not the tail any more, and the typing controls still work.
      setRun((prev) =>
        prev && prev.idx === idxAtRequest && prev.verdict === null ? replaceTail(prev, list) : prev,
      );
    } catch {
      // Nothing to say: `optionsMode` with no options now falls back to typing (see `showingOptions`).
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        background: 'var(--nn-bg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', height: 44, padding: '0 4px', gap: 2 }}>
        <UnstyledButton
          onClick={() => navigate(-1)}
          aria-label="Back"
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--nn-text)',
          }}
        >
          <IconBack />
        </UnstyledButton>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--nn-text-strong)' }}>
          Review{' '}
          <span style={{ color: 'var(--nn-muted)', fontWeight: 400 }}>
            · {run.idx + 1} of {run.exercises.length}
          </span>
        </span>
        <span style={{ flex: 1 }} />
        {!run.verdict && (
          <UnstyledButton
            onClick={onSkip}
            style={{ fontSize: 12, color: 'var(--nn-muted)', padding: '0 12px' }}
          >
            Skip
          </UnstyledButton>
        )}
      </div>

      {/* `5d`: the prompt hugs the keyboard. The spacer is what puts it there. */}
      <span style={{ flex: 1 }} />

      <div style={{ padding: '0 18px 12px' }}>
        {ex.kind === 'listen_repeat' ? (
          <ListenRepeat
            ex={ex}
            index={run.idx}
            total={run.exercises.length}
            // Submit AND advance. A listen & repeat prompt is never graded, so there is no
            // verdict worth showing — and rendering one put a second Next on the screen beside
            // this one, whose every press wrote another retrieval_attempt and moved nothing.
            onNext={() => void submit('', 'typed', { advance: true })}
          />
        ) : ex.kind === 'dictation' ? (
          <Dictation ex={ex} typed={typed} verdict={run.verdict} onCalm={setCalm} />
        ) : (
          <Cloze ex={ex} typed={typed} verdict={run.verdict} optionsMode={showingOptions} />
        )}

        {run.offerState === 'showing' && !run.verdict && (
          <OptionsOffer
            onAccept={() => void onAcceptOptions()}
            onDismiss={() => setRun(dismissOptions(run))}
          />
        )}

        {/* Not for listen & repeat: there is nothing to type and nothing to show, and the
            autoFocus'd Georgian input popped the keyboard open on a hold-to-record prompt. */}
        {!run.verdict && ex.kind !== 'listen_repeat' && (
          <AnswerControls
            ex={ex}
            typed={typed}
            optionsMode={showingOptions}
            busy={busy}
            onType={(v, firstChar) => {
              setTyped(v);
              if (firstChar) setRun(keystroke(run, firstChar));
            }}
            onSubmit={(answer, mode) => void submit(answer, mode)}
            onShow={() => void submit('', 'shown')}
          />
        )}

        {run.verdict && <Verdict verdict={run.verdict} ex={ex} onNext={onNext} />}

        {calm && (
          <Text fz={11} c="var(--nn-muted)" mt={8} role="status">
            {calm}
          </Text>
        )}

        <SourceLink ex={ex} />
      </div>
    </div>
  );
}

/** The sentence with its blank (`5d`). The caret is drawn only where the learner is typing. */
function Cloze({
  ex,
  typed,
  verdict,
  optionsMode,
}: {
  ex: Exercise;
  typed: string;
  verdict: RunState['verdict'];
  optionsMode: boolean;
}) {
  const text = ex.prompt.text ?? '';
  const at = text.indexOf(BLANK);
  const hint = ex.prompt.blanks?.[0]?.hint;
  const label =
    ex.kind === 'pattern_completion'
      ? 'COMPLETE THE PATTERN'
      : ex.kind === 'comprehension'
        ? `QUICK CHECK`
        : 'FILL THE BLANK';

  return (
    <>
      <div
        style={{
          font: '10px/1 ui-monospace, Menlo, monospace',
          color: 'var(--nn-faint)',
          letterSpacing: '.02em',
        }}
      >
        {label}
      </div>
      <div
        lang="ka"
        style={{
          marginTop: 9,
          fontSize: 17,
          lineHeight: 1.66,
          color: 'var(--nn-text-strong)',
          fontFamily: "'Noto Serif Georgian', serif",
        }}
      >
        {at === -1 ? (
          text
        ) : (
          <>
            {text.slice(0, at)}
            <span
              style={{
                display: 'inline-block',
                minWidth: 110,
                borderBottom: '1.5px solid color-mix(in srgb, var(--nn-text) 25%, transparent)',
                textAlign: 'center',
                color: 'var(--nn-text-strong)',
              }}
            >
              {typed}
              {!verdict && !optionsMode && (
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block',
                    width: 1.5,
                    height: 16,
                    background: 'var(--nn-accent)',
                    verticalAlign: -2,
                  }}
                />
              )}
            </span>
            {text.slice(at + BLANK.length)}
          </>
        )}
      </div>
      {hint && <div style={{ marginTop: 5, fontSize: 12, color: 'var(--nn-muted)' }}>{hint}</div>}
    </>
  );
}

/**
 * Dictation (`5e`): a play pill and a box. The line itself is never shown before the answer —
 * that is the whole prompt. After submitting, the learner's own text is shown back with only the
 * wrong tokens underlined in the ERROR tint, never the accent: the accent is what the app uses
 * for "this is yours", and painting a mistake with it would be a small cruelty.
 */
function Dictation({
  ex,
  typed,
  verdict,
  onCalm,
}: {
  ex: Exercise;
  typed: string;
  verdict: RunState['verdict'];
  onCalm: (s: string | null) => void;
}) {
  const play = async () => {
    if (!ex.prompt.audio) return;
    onCalm(null);
    try {
      await ttsPlayer.play({
        url: ex.prompt.audio.url,
        title: 'Dictation',
        subtitle: 'review',
        key: `review:${ex.id}`,
      });
    } catch (err) {
      onCalm(ttsCalmLine(err));
    }
  };
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <UnstyledButton
          onClick={() => void play()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 12px',
            borderRadius: 999,
            border: '1px solid color-mix(in srgb, var(--nn-text) 14%, transparent)',
            color: 'var(--nn-text)',
            fontSize: 11.5,
          }}
        >
          <span style={{ display: 'flex', transform: 'scale(.45)', width: 10, height: 10 }}>
            <IconPlay />
          </span>
          play again
        </UnstyledButton>
        <span style={{ font: '10px/1 ui-monospace, Menlo, monospace', color: 'var(--nn-faint)' }}>
          DICTATION
        </span>
      </div>
      {verdict && <DictationDiff expected={String(verdict.expected ?? '')} typed={typed} />}
    </>
  );
}

/**
 * The error tint, for the dictation diff only.
 *
 * `theme.ts` is a protected artifact and has no error token, so this follows the precedent
 * `Words.tsx` set for its one destructive action: a `light-dark()` pair, whose dark value is the
 * canvas's own `#e0938c` (`5e`). It is deliberately NOT the accent — the accent means "this is
 * yours", and painting a mistake with it would be a small cruelty.
 */
// M6c: the pair moved to `lib/error-tint.ts` when the journal became its fourth surface. The
// comment above is kept because it is the reason, and the reason did not move.

/** The 5e diff: the learner's own words, with the ones that matched nothing underlined. */
function DictationDiff({ expected, typed }: { expected: string; typed: string }) {
  const wrong = useMemo(() => wrongTokenIndices(expected, typed), [expected, typed]);
  // The SAME tokens the indices are counted over. Splitting the raw string here instead would
  // drift the two lists apart the moment the learner typed a standalone punctuation mark — a
  // leading dash, a spaced ellipsis — and underline the word next door.
  const tokens = useMemo(() => answerTokens(typed), [typed]);
  return (
    <div
      lang="ka"
      style={{
        marginTop: 11,
        fontSize: 15,
        lineHeight: 1.6,
        fontFamily: "'Noto Serif Georgian', serif",
      }}
    >
      {tokens.map((t, i) => (
        <span
          key={`${t}-${i}`}
          style={
            wrong.has(i)
              ? {
                  textDecorationLine: 'underline',
                  textDecorationColor: ERROR_RULE,
                  textDecorationThickness: 1.5,
                  textUnderlineOffset: '0.2em',
                  textDecorationSkipInk: 'none',
                  color: ERROR_INK,
                }
              : undefined
          }
        >
          {t}
          {i < tokens.length - 1 ? ' ' : ''}
        </span>
      ))}
    </div>
  );
}

/**
 * The tokens a dictation answer is compared and drawn as — one list, used for both.
 *
 * Exported so `DictationDiff` renders exactly what `wrongTokenIndices` counted. See the note on
 * the character class below for why it is written in escapes.
 */
export function answerTokens(s: string): string[] {
  return s
    .normalize('NFC')
    .replace(
      /[\u0021-\u002C\u002E-\u002F\u003A-\u0040\u005B-\u0060\u007B-\u007E\u2000-\u206F\u10FB\u00AB\u00BB\u201E\u201C]/gu,
      '',
    )
    .replace(/\s+/gu, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
}

/**
 * Which of the learner's tokens matched nothing, by the same LCS the server grades with.
 *
 * Duplicated here rather than imported from the worker because the two packages share no code at
 * runtime (the tokenizer is the one exception, and it is aliased deliberately). The comparison is
 * only cosmetic on this side — the verdict came from the server — so a disagreement costs an
 * underline, not a grade.
 *
 * The class is written in ESCAPES, not literal characters, and that is the whole point. This copy
 * drifted from the worker's by ONE code point — a literal U+0020 where the worker has U+2000 —
 * which made the range `U+0020-U+206F` and swallowed the entire Mkhedruli block. `norm()` returned
 * the empty string for any Georgian input, both token lists came out empty, and the 5e error tint
 * underlined nothing on every dictation ever shown. The two source lines were INDISTINGUISHABLE
 * on screen, which is why it survived review; escapes are legible, literal punctuation is not.
 */
export function wrongTokenIndices(expected: string, typed: string): Set<number> {
  const a = answerTokens(expected).map((t) => t.toLowerCase());
  const b = answerTokens(typed).map((t) => t.toLowerCase());
  const table: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i]![j] =
        a[i] === b[j] ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const matched = new Set<number>();
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      matched.add(j);
      i++;
      j++;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) i++;
    else j++;
  }
  const out = new Set<number>();
  for (let k = 0; k < b.length; k++) if (!matched.has(k)) out.add(k);
  return out;
}

/** The typing box, or the chips. Never both. */
function AnswerControls({
  ex,
  typed,
  optionsMode,
  busy,
  onType,
  onSubmit,
  onShow,
}: {
  ex: Exercise;
  typed: string;
  optionsMode: boolean;
  busy: boolean;
  onType: (v: string, firstChar?: string) => void;
  onSubmit: (answer: unknown, mode: 'typed' | 'options') => void;
  onShow: () => void;
}) {
  const options = ex.prompt.options ?? [];
  return (
    <>
      {optionsMode && options.length > 0 ? (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 11 }}>
          {options.map((o, i) => (
            <UnstyledButton
              key={o}
              lang="ka"
              disabled={busy}
              onClick={() => onSubmit(ex.kind === 'comprehension' ? i : o, 'options')}
              style={{
                fontSize: 14,
                padding: '7px 13px',
                borderRadius: 7,
                border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
                color: 'var(--nn-text)',
              }}
            >
              {o}
            </UnstyledButton>
          ))}
        </div>
      ) : (
        <input
          lang="ka"
          value={typed}
          disabled={busy}
          autoFocus
          inputMode="text"
          aria-label="Your answer"
          onChange={(e) => {
            const v = e.target.value;
            // Only the FIRST character of an empty box is evidence about the keyboard.
            onType(v, typed.length === 0 && v.length > 0 ? v[0] : undefined);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && typed.trim()) onSubmit(typed, 'typed');
          }}
          style={{
            marginTop: 12,
            width: '100%',
            background: 'transparent',
            border: 0,
            borderBottom: '1px solid var(--nn-hairline)',
            color: 'var(--nn-text-strong)',
            fontSize: 16,
            fontFamily: "'Noto Serif Georgian', serif",
            padding: '6px 2px',
            outline: 'none',
          }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 11 }}>
        <UnstyledButton onClick={onShow} style={{ fontSize: 12, color: 'var(--nn-muted)' }}>
          Show answer
        </UnstyledButton>
        <span style={{ flex: 1 }} />
        {!optionsMode && typed.trim() && (
          <UnstyledButton
            disabled={busy}
            onClick={() => onSubmit(typed, 'typed')}
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              padding: '7px 16px',
              borderRadius: 7,
              background: 'var(--nn-accent)',
              color: 'var(--nn-on-accent)',
            }}
          >
            Check
          </UnstyledButton>
        )}
      </div>
    </>
  );
}

/** Offered once per run, on the first non-Georgian keystroke (`5e`). */
function OptionsOffer({ onAccept, onDismiss }: { onAccept: () => void; onDismiss: () => void }) {
  return (
    <div style={{ marginTop: 9 }}>
      <span style={{ fontSize: 11.5, color: 'var(--nn-muted)' }}>
        Not typing Georgian right now? Answer with options —
      </span>{' '}
      <UnstyledButton
        onClick={onAccept}
        style={{ fontSize: 11.5, color: 'var(--nn-link)', display: 'inline' }}
      >
        yes
      </UnstyledButton>
      <span style={{ fontSize: 11.5, color: 'var(--nn-faint)' }}> · </span>
      <UnstyledButton
        onClick={onDismiss}
        style={{ fontSize: 11.5, color: 'var(--nn-faint)', display: 'inline' }}
      >
        no
      </UnstyledButton>
    </div>
  );
}

function Verdict({
  verdict,
  ex,
  onNext,
}: {
  verdict: NonNullable<RunState['verdict']>;
  ex: Exercise;
  onNext: () => void;
}) {
  // Resolved, not stringified: for a comprehension check `expected` is the option INDEX.
  const shown = expectedText(ex, verdict.expected);
  return (
    <div style={{ marginTop: 11 }}>
      <div
        style={{
          fontSize: 12.5,
          color: verdict.correct ? 'var(--nn-accent-text)' : 'var(--nn-muted)',
        }}
      >
        {verdict.correct ? 'Right' : shown === null ? 'Not quite' : 'The answer was'}
        {!verdict.correct && shown !== null && (
          <>
            {' '}
            <span lang="ka" style={{ color: 'var(--nn-text-strong)' }}>
              {shown}
            </span>
          </>
        )}
        {verdict.explanation ? ` — ${verdict.explanation}` : ''}
      </div>
      <UnstyledButton
        onClick={onNext}
        style={{
          marginTop: 12,
          fontSize: 12.5,
          fontWeight: 600,
          padding: '7px 16px',
          borderRadius: 7,
          background: 'var(--nn-accent)',
          color: 'var(--nn-on-accent)',
        }}
      >
        Next
      </UnstyledButton>
    </div>
  );
}

/**
 * "watch the source line →" (`5d`). Opens Watch seeked to the line, never an inline embed: the
 * player is a guest and there is exactly one place it lives.
 */
function SourceLink({ ex }: { ex: Exercise }) {
  const lineId = ex.source?.lineId ?? null;
  const [ref, setRef] = useState<LineRef | null>(null);
  useEffect(() => {
    setRef(null);
    if (!lineId) return;
    let cancelled = false;
    void api
      .lineGet(lineId)
      .then((r) => {
        if (!cancelled) setRef(r);
      })
      // A line whose video has gone away (rule 4) simply has no link; nothing to say about it.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [lineId]);
  if (!ref) return null;
  return (
    <a
      href={`/watch/${ref.videoId}?t=${ref.startMs}`}
      style={{ display: 'inline-block', marginTop: 11, fontSize: 11, color: 'var(--nn-faint)' }}
    >
      watch the source line →
    </a>
  );
}

// ---------------------------------------------------------------------------------------------
// listen & repeat (12c)
// ---------------------------------------------------------------------------------------------

/**
 * Hold to record, then play both (`12c`).
 *
 * The recording is a blob URL and **never leaves the device**: there is no upload, no bucket and
 * no route that would accept one. Until the STT bake-off says Georgian speech recognition is
 * usable there is nothing that could score it, and storing audio nobody can use would be
 * collecting a learner's voice for its own sake.
 */
function ListenRepeat({
  ex,
  index,
  total,
  onNext,
}: {
  ex: Exercise;
  index: number;
  total: number;
  onNext: () => void;
}) {
  const [mine, setMine] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [calm, setCalm] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Revoking matters: a run of six prompts would otherwise leak six recordings for the life of
  // the tab, and they are the largest objects this screen touches.
  useEffect(() => {
    return () => {
      if (mine) URL.revokeObjectURL(mine);
    };
  }, [mine]);

  const start = async () => {
    setCalm(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = () => {
        for (const t of stream.getTracks()) t.stop();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setMine((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      setCalm('The microphone isn’t available. You can still listen.');
    }
  };
  const stop = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  const playLine = async () => {
    if (!ex.prompt.audio) return;
    try {
      await ttsPlayer.play({
        url: ex.prompt.audio.url,
        title: 'Listen & repeat',
        subtitle: 'review',
        key: `lr:${ex.id}`,
      });
    } catch (err) {
      setCalm(ttsCalmLine(err));
    }
  };

  return (
    <div>
      <div style={{ font: '10px/1 ui-monospace, Menlo, monospace', color: 'var(--nn-faint)' }}>
        LISTEN &amp; REPEAT · {index + 1} OF {total}
      </div>
      <div
        lang="ka"
        style={{
          marginTop: 10,
          fontSize: 17,
          lineHeight: 1.6,
          fontFamily: "'Noto Serif Georgian', serif",
          color: 'var(--nn-text-strong)',
        }}
      >
        {ex.prompt.text}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12 }}>
        <UnstyledButton onClick={() => void playLine()} style={pill}>
          <span style={{ display: 'flex', transform: 'scale(.45)', width: 10, height: 10 }}>
            <IconPlay />
          </span>
          hear it
        </UnstyledButton>
        <UnstyledButton
          onPointerDown={() => void start()}
          onPointerUp={stop}
          onPointerLeave={() => recording && stop()}
          style={{
            ...pill,
            background: recording
              ? 'color-mix(in srgb, var(--nn-accent) 18%, transparent)'
              : undefined,
            color: recording ? 'var(--nn-accent-text)' : 'var(--nn-text)',
          }}
        >
          {recording ? 'recording…' : 'hold to record'}
        </UnstyledButton>
      </div>
      {mine && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 11, color: 'var(--nn-faint)' }}>
            your take — stays on this device
          </span>
          <audio src={mine} controls style={{ width: '100%', maxWidth: 280 }} />
        </div>
      )}
      {calm && (
        <Text fz={11} c="var(--nn-muted)" mt={8} role="status">
          {calm}
        </Text>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14 }}>
        <span style={{ flex: 1 }} />
        <UnstyledButton
          onClick={onNext}
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            padding: '7px 16px',
            borderRadius: 7,
            background: 'var(--nn-accent)',
            color: 'var(--nn-on-accent)',
          }}
        >
          Next
        </UnstyledButton>
      </div>
    </div>
  );
}

const pill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '5px 13px',
  borderRadius: 999,
  border: '1px solid color-mix(in srgb, var(--nn-text) 14%, transparent)',
  color: 'var(--nn-text)',
  fontSize: 11.5,
};

// ---------------------------------------------------------------------------------------------
// the end
// ---------------------------------------------------------------------------------------------

/** `5e`: numbers, no fanfare. */
function RunSummary({ run, onDone }: { run: RunState; onDone: () => void }) {
  const s = summarise(run);
  return (
    <Shell onBack={onDone} title="Review">
      <div
        style={{
          background: 'var(--nn-panel)',
          border: '1px solid var(--nn-hairline)',
          borderRadius: 12,
          padding: 16,
          marginTop: 12,
        }}
      >
        <div style={{ fontSize: 14.5, color: 'var(--nn-text-strong)' }}>{summaryLine(s)}</div>
        <UnstyledButton
          onClick={onDone}
          style={{
            display: 'inline-flex',
            marginTop: 10,
            padding: '7px 14px',
            borderRadius: 7,
            border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
            fontSize: 12,
            color: 'var(--nn-text)',
          }}
        >
          Done
        </UnstyledButton>
      </div>
    </Shell>
  );
}

function Shell({
  onBack,
  title,
  children,
}: {
  onBack: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', height: 44, padding: '0 4px', gap: 2 }}>
        <UnstyledButton
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--nn-text)',
          }}
        >
          <IconBack />
        </UnstyledButton>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--nn-text-strong)' }}>
          {title}
        </span>
      </div>
      <div style={{ padding: '0 18px' }}>{children}</div>
    </div>
  );
}

/** Skeletons, never spinners (design README §6). */
function RunSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <Shell onBack={onBack} title="Review">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
        <Skeleton height={11} width="35%" radius="sm" />
        <Skeleton height={22} width="90%" radius="sm" />
        <Skeleton height={14} width="60%" radius="sm" />
      </div>
    </Shell>
  );
}
